/**
 * Form-side model of a connection's `normalize` rules (crates/tools
 * `handlers/normalize.rs`): first matching rule wins; each states facts
 * about a delivery. Rules a setup flow or recipe owns (`profile`) and rules
 * the editor can't represent are kept verbatim, in place.
 */
import { type TermRow, termBody, termRow, termRows } from './predicate'

export type EntityFact = '' | 'closed' | 'updated' | 'reopened'

export interface RuleDraft {
  /** A rule kept verbatim (owned by a profile, or beyond the editor). */
  kept?: Record<string, unknown>
  conditions: TermRow[]
  /** The first condition is stored at the rule's top level (the rest
   * under `all`) — kept as the rule had it, so an edit doesn't reshape it. */
  topLevel: boolean
  event_type: string
  correlation_key: string
  dedup_key: string
  agent_message: string
  entity: EntityFact
  drop: boolean
}

export const BLANK_RULE: RuleDraft = {
  conditions: [],
  topLevel: true,
  event_type: '',
  correlation_key: '',
  dedup_key: '',
  agent_message: '',
  entity: '',
  drop: false,
}

const FACT_KEYS = [
  'event_type',
  'correlation_key',
  'dedup_key',
  'agent_message',
] as const
const PREDICATE_KEYS = [
  'when',
  'equals',
  'not_equals',
  'in',
  'in_ci',
  'contains',
  'exists',
]
const EDITABLE_KEYS = new Set([
  ...PREDICATE_KEYS,
  'all',
  ...FACT_KEYS,
  'entity',
  'action',
])

const str = (v: unknown) => (typeof v === 'string' ? v : '')

/** A stored rule as a draft; profile-owned or unrepresentable rules are
 * kept verbatim. */
export function ruleDraft(raw: unknown): RuleDraft {
  const r = (raw ?? {}) as Record<string, unknown>
  const kept = { ...BLANK_RULE, kept: r }
  if (r.profile != null) return kept
  if (Object.keys(r).some((k) => !EDITABLE_KEYS.has(k))) return kept
  const top = Object.fromEntries(
    PREDICATE_KEYS.filter((k) => r[k] != null).map((k) => [k, r[k]]),
  )
  const topRow = Object.keys(top).length > 0 ? termRow(top) : null
  if (Object.keys(top).length > 0 && !topRow) return kept
  const allRows = r.all == null ? [] : termRows(r.all)
  if (!allRows) return kept
  return {
    conditions: topRow ? [topRow, ...allRows] : allRows,
    topLevel: topRow !== null,
    event_type: str(r.event_type),
    correlation_key: str(r.correlation_key),
    dedup_key: str(r.dedup_key),
    agent_message: str(r.agent_message),
    entity: (str(r.entity) as EntityFact) || '',
    drop: r.action === 'drop',
  }
}

/** A draft as a stored rule: the first condition at the top level when
 * the draft keeps it there, the rest under `all`. */
export function ruleBody(d: RuleDraft): Record<string, unknown> {
  if (d.kept) return d.kept
  const terms = d.conditions.map(termBody).filter((t) => t !== null)
  const [first, ...rest] = terms
  const top = d.topLevel && first ? first : null
  const all = top ? rest : terms
  const rule: Record<string, unknown> = { ...(top ?? {}) }
  if (all.length > 0) rule.all = all
  for (const key of FACT_KEYS) {
    const v = d[key].trim()
    if (v) rule[key] = v
  }
  if (d.entity) rule.entity = d.entity
  if (d.drop) rule.action = 'drop'
  return rule
}

export function ruleDrafts(rules: unknown): RuleDraft[] {
  return Array.isArray(rules) ? rules.map(ruleDraft) : []
}
